import { Globe } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Callout } from '@/components/ui/feedback';
import {
  Pill,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/primitives';
import { useParticipants } from '@/lib/api/ops-queries';
import { ALL_WEBS, useWebScope } from '@/lib/scope/web-scope';
import { cn } from '@/lib/utils';

/**
 * How one site is named in the list.
 *
 * The id is appended so an operator can match what they pick against the id every row, log line
 * and support ticket uses — but only when the display name does not already carry it. Several
 * participants are registered with names like `Chao Phraya Gaming (web_b)`, and appending
 * unconditionally turned those into `Chao Phraya Gaming (web_b) (web_b)`.
 */
/** One row of the menu, on the sidebar's palette rather than the page's. */
const ITEM = 'focus:bg-white/10 focus:text-sidebar-foreground';

function labelFor(name: string, id: string): string {
  return name === id || name.includes(id) ? name : `${name} (${id})`;
}

/**
 * The console-wide website selector.
 *
 * One control, in the shell, for the scope every operator read then narrows to — see
 * `lib/scope/web-scope.tsx` for why the scope is a session preference rather than a per-screen
 * filter. Rendered in the sidebar on desktop and in the mobile menu, both times *above* the
 * navigation: an operator who has scoped the console must be able to see that they have, without
 * scrolling and without opening a menu. A filter you cannot see is how someone reads one site's
 * numbers as the platform's.
 *
 * It is built to read as part of the navigation rail rather than as a form field that landed in
 * it. `SelectTrigger`'s own surface is `bg-background` over `border-input` — page tokens, which
 * under the light theme paint a white box into a permanently dark sidebar — so the trigger is
 * re-skinned onto the `sidebar-*` tokens the rail's own chrome uses, and laid out as caption-over-
 * value behind a leading icon, which is the shape every nav item below it already has.
 *
 * Three details are deliberate:
 *
 * - **The globe and the border carry the accent only while the console is scoped.** The control
 *   has to be legible as *on* at a glance from across the rail, without shouting in the state it
 *   spends most of its life in.
 *
 * - **A stored scope naming a site that no longer exists is still offered**, as its bare id. The
 *   directory can lose a participant while a browser still remembers it, and Radix's `Select`
 *   renders an empty trigger for a value with no matching item — so the operator would see a blank
 *   control while every screen behind it showed an empty pool. Better to name the unknown scope so
 *   it can be recognised and cleared.
 * - **The directory failing to load does not disable the control.** The list of sites is a
 *   convenience; the scope is already known. If `/participants` is down, an operator can still see
 *   what they are scoped to and still return to the whole pool.
 */
export function WebScopeSelect({ className }: { className?: string | undefined }): ReactNode {
  const { t } = useTranslation();
  const { scope, setScope } = useWebScope();
  const participants = useParticipants();

  const items = participants.data?.items ?? [];
  const known = items.some((participant) => participant.id === scope);
  const scoped = scope !== ALL_WEBS;
  // The rail is 256px wide and a site's name plus its id rarely fits, so the value line truncates.
  // A `title` keeps the whole label recoverable without opening the menu — which matters because
  // the half that gets cut is the id, and the id is what every row, log line and ticket uses.
  const current = items.find((participant) => participant.id === scope);
  const currentLabel =
    current === undefined ? undefined : labelFor(current.name, current.id);

  return (
    <div className={className}>
      <Select value={scope} onValueChange={setScope}>
        <SelectTrigger
          id="web-scope"
          aria-label={t('layout.shell.webScope.ariaLabel')}
          className={cn(
            // `h-auto` and `line-clamp-none` undo two of the primitive's assumptions: a form
            // field's fixed height, and the single-line clamp it puts on its first child — which
            // here is the caption-over-value stack, not the value.
            'h-auto items-center gap-3 rounded-lg px-3 py-2 text-left shadow-none transition-colors',
            '[&>span]:line-clamp-none',
            'border-sidebar-border bg-white/5 text-sidebar-foreground hover:bg-white/10',
            'focus:ring-sidebar-accent focus:ring-offset-0',
            scoped && 'border-sidebar-accent/50 bg-sidebar-accent/10 hover:bg-sidebar-accent/15',
            '[&>svg]:text-sidebar-muted',
          )}
        >
          <Globe
            aria-hidden="true"
            className={cn('size-4 shrink-0', scoped ? 'text-sidebar-accent' : 'text-sidebar-muted')}
          />
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block text-2xs uppercase tracking-wide text-sidebar-muted">
              {t('layout.shell.webScope.label')}
            </span>
            <span className="block truncate text-sm font-medium" title={currentLabel}>
              <SelectValue />
            </span>
          </span>
        </SelectTrigger>
        {/* The menu drops over the rail, not over the page, so it wears the rail's colours rather
            than the page's — under the light theme the default `bg-popover` is a white sheet
            unfolding out of dark chrome. */}
        <SelectContent
          className={cn(
            'border-sidebar-border bg-sidebar text-sidebar-foreground',
            '[&_[role=option]]:focus:bg-white/10',
          )}
        >
          <SelectItem value={ALL_WEBS} className={ITEM}>
            {t('layout.shell.webScope.all')}
          </SelectItem>
          {items.map((participant) => (
            <SelectItem key={participant.id} value={participant.id} className={ITEM}>
              {labelFor(participant.name, participant.id)}
            </SelectItem>
          ))}
          {known || scope === ALL_WEBS ? null : (
            <SelectItem value={scope} className={ITEM}>
              {t('layout.shell.webScope.unknown', { id: scope })}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * A compact read-out of the current scope, for chrome too narrow to hold the selector.
 *
 * Renders nothing at all when the console is showing the whole pool — the unscoped console is the
 * normal state and does not need announcing. It exists so that on a phone, where the selector
 * lives behind the menu button, the scope is still on screen: the failure this whole feature can
 * cause is an operator reading one site's numbers as the platform's, and that failure is silent
 * unless the scope is visible.
 */
export function WebScopeIndicator({ className }: { className?: string | undefined }): ReactNode {
  const { t } = useTranslation();
  const { scope } = useWebScope();
  if (scope === ALL_WEBS) return null;
  return (
    <Pill className={cn('shrink-0 bg-sidebar-accent/15 text-sidebar-accent', className)}>
      <Globe aria-hidden="true" className="mr-1 size-3" />
      <span className="sr-only">{t('layout.shell.webScope.label')}: </span>
      {scope}
    </Pill>
  );
}

/**
 * The reminder a scoped page shows so its numbers are never mistaken for the platform's.
 *
 * The scope narrows every read on a page silently — that is the point of it living in the query
 * hooks rather than a filter each screen wires up (`lib/scope/web-scope.tsx`) — but silence is
 * exactly the problem the moment somebody reads a scoped tile as an unscoped one. The shell's
 * selector and indicator are always on screen, but a KPI grid or a positions table can be scrolled
 * to well past them, so the page that actually narrowed says so again, right where the numbers it
 * is describing begin.
 *
 * Renders nothing on the unscoped console — the default, and the state that needs no caption —
 * so mounting this unconditionally at the top of a scoped section is always correct.
 */
export function WebScopeScopedNotice({ className }: { className?: string | undefined }): ReactNode {
  const { t } = useTranslation();
  const { scope } = useWebScope();
  if (scope === ALL_WEBS) return null;
  return (
    <Callout tone="info" className={className}>
      {t('common.webScope.scopedNotice', { web: scope })}
    </Callout>
  );
}
